import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Loader2, Send, Sparkles, FileText } from 'lucide-react'
import { trpc } from '@/lib/trpc/client'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

export default function NewProposalPage() {
  const navigate = useNavigate()
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Hello! I\'m your Sika AI assistant for complete construction solutions. I\'ll ask you a few questions to prepare a comprehensive specification with all necessary systems—from foundation to finishes. Let\'s start: What type of project do you have in mind?',
    },
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const consultationMutation = trpc.consultation.chat.useMutation()

  const generateProposal = () => {
    const messagesParam = encodeURIComponent(JSON.stringify(messages))
    navigate(`/proposal/review?messages=${messagesParam}`)
  }

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return

    const userMessage: Message = { role: 'user', content: input }
    const updatedMessages = [...messages, userMessage]
    setMessages(updatedMessages)
    setInput('')
    setIsLoading(true)

    try {
      const result = await consultationMutation.mutateAsync({
        messages: updatedMessages,
      })

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.content,
        },
      ])
    } catch (error) {
      console.error('Error sending message:', error)
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, an error occurred. Please try again.',
        },
      ])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="inline-block">
                <img
                  src="/sika-logo.svg"
                  alt="Sika"
                  width={100}
                  height={33}
                  className="opacity-80 hover:opacity-100 transition-opacity"
                />
              </a>
              <div className="border-l pl-4">
                <h1 className="text-2xl font-bold text-slate-900">New Proposal</h1>
                <p className="text-sm text-slate-600">Complete Construction System</p>
              </div>
            </div>
            <Button variant="outline" asChild>
              <a href="/">Back</a>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Chat Area */}
          <div className="md:col-span-2">
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="border-b bg-gradient-to-r from-red-50 to-slate-50">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#D32F2F]" />
                  <CardTitle>Intelligent Consultation</CardTitle>
                </div>
                <CardDescription>
                  Answer questions to receive a complete specification with recommended products
                </CardDescription>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 p-0 overflow-hidden">
                <ScrollArea className="h-full p-6" ref={scrollRef}>
                  <div className="space-y-4">
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${
                          message.role === 'user' ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-3 ${
                            message.role === 'user'
                              ? 'bg-[#D32F2F] text-white'
                              : 'bg-slate-100 text-slate-900'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                      </div>
                    ))}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="bg-slate-100 rounded-lg px-4 py-3">
                          <Loader2 className="w-5 h-5 animate-spin text-slate-600" />
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>

              {/* Input */}
              <div className="border-t p-4 bg-white">
                {messages.filter(m => m.role === 'user').length >= 3 && (
                  <div className="mb-4">
                    <Button
                      onClick={generateProposal}
                      className="w-full"
                      size="lg"
                      variant="default"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Generate My Proposal
                    </Button>
                    <p className="text-xs text-center text-slate-500 mt-2">
                      Or continue the conversation for more details
                    </p>
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    placeholder="Type your answer here..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    className="flex-1"
                  />
                  <Button onClick={sendMessage} disabled={isLoading || !input.trim()}>
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Press Enter to send • Shift+Enter for new line
                </p>
              </div>
            </Card>
          </div>

          {/* Sidebar - Progress & Info */}
          <div className="space-y-6">
            {/* Progress */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Consultation Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Questions answered</span>
                    <span className="font-semibold">{Math.floor(messages.filter(m => m.role === 'user').length / 2)} / 4-6</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#D32F2F] transition-all duration-300"
                      style={{
                        width: `${Math.min((messages.filter(m => m.role === 'user').length / 6) * 100, 100)}%`,
                      }}
                    />
                  </div>
                </div>

                <div className="pt-4 border-t space-y-2">
                  <p className="text-sm font-medium text-slate-900">What's next:</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>✓ Answer 4-6 questions</li>
                    <li>→ Receive product recommendations</li>
                    <li>→ Customize quantities</li>
                    <li>→ Generate PDF specification</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Help */}
            <Card className="bg-orange-50 border-orange-200">
              <CardHeader>
                <CardTitle className="text-lg text-orange-900">💡 Tip</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-orange-800">
                  Be as specific as possible with dimensions and project details.
                  The more precise the information, the more accurate the specification!
                </p>
              </CardContent>
            </Card>

            {/* Templates Quick Start */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Example Answers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-left h-auto py-2"
                  onClick={() => setInput('Waterproofing for bathroom, 6sqm')}
                >
                  <span className="text-sm">Bathroom 6sqm</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-left h-auto py-2"
                  onClick={() => setInput('Exterior terrace 25sqm, exposed to sun')}
                >
                  <span className="text-sm">Terrace 25sqm</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-left h-auto py-2"
                  onClick={() => setInput('Swimming pool 50sqm, complete renovation')}
                >
                  <span className="text-sm">Pool 50sqm</span>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  )
}
